import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { Button, Divider, message, Form, Input, Select, Spin, Tag } from "antd";
import {
  EntityStatus,
  ConfigActions,
  ConfigEntity,
  formItemLayout,
  tailItemLayout,
  ConfigJSON,
  GroupPurpose,
} from "../../../Constants/Constants";
import {
  jsonValidator,
  selectFilterFunction,
} from "../../../Constants/Utilities";
import { apiCallback } from "../../../ApiHandlers/Common";
import { getConfigFormTitle } from "../../../Helpers/ConfigHelpers";
import { onFormSubmit } from "../../../ApiHandlers/ConfigApiHandlers";
import { convertServiceSimpleList } from "../../../Helpers/ServiceHelpers";
import { convertGroupListToDropdown } from "../../../Helpers/GroupHelpers";
import { convertDeviceModelListToDropdown } from "../../../Helpers/DeviceModelHelpers";
import { DiscardWarning } from "../../Common/DiscardWarning";
import { BreadCrumbs } from "../../Common/BreadCrumbs";

const getInitialValues = (record) => {
  const initialValues = {
    status: EntityStatus.ACTIVE,
    config: ConfigJSON,
  };
  if (record) {
    initialValues.name = record.name;
    initialValues.status = record.status;
    initialValues.providerId = record.providerId;
    initialValues.groupId = record.groupId;
    initialValues.serviceId = record.serviceId;
    initialValues.modelId = record.deviceModelId;
    initialValues.config = JSON.stringify(record.config, null, 4);
  }
  return initialValues;
};

const GroupConfigForm = (props) => {
  console.log("GroupConfigForm  props", props);
  const { action, history, record, onActionClick, deviceModelApi, serviceApi } =
    props;

  const title = useMemo(() => getConfigFormTitle(action), [action]);
  const [initialValues, setInitialValues] = useState(getInitialValues(record));

  // service info
  const [serviceDataLoading, setserviceDataLoading] = useState(false);
  const [serviceData, setserviceData] = useState([]);

  //model info
  const [modelData, setmodelData] = useState([]);
  const [modelDataLoading, setmodelDataLoading] = useState(false);

  const [form] = Form.useForm();
  const [selectedProvider, setSelectedProvider] = useState(null);
  // group Info
  const [groupData, setGroupData] = useState([]);
  const [groupDataLoading, setGroupDataLoading] = useState(false);

  const [processing, setProcessing] = useState(false);
  const isAddForm = props.action === ConfigActions.add;
  const isViewForm = props.action === ConfigActions.view;

  useEffect(() => {
    updateModelList();
    updateServiceList();
    initialValues.providerId && updateGroupList(initialValues.providerId);
  }, []);

  const updateServiceList = () => {
    console.log("fetchserviceList...");
    setserviceDataLoading(true);
    const errorCallback = (error) => {
      setserviceDataLoading(false);
      message.error("Error fetching service list");
    };
    const successCallback = (data) => {
      console.log("fetchDeviceModelList data", data);
      const serviceData = convertServiceSimpleList(data);
      setserviceDataLoading(false);
      console.log("serviceData", serviceData);
      setserviceData(serviceData);
    };
    try {
      serviceApi.getService(
        apiCallback({ history, errorCallback, successCallback })
      );
    } catch (error) {
      errorCallback(error);
    }
  };

  const updateModelList = () => {
    setmodelDataLoading(true);
    const errorCallback = (error) => {
      setmodelDataLoading(false);
      message.error("Error fetching Device Model list");
      console.log("Model Error", error);
    };
    const successCallback = (data) => {
      const modelData = convertDeviceModelListToDropdown(data);
      console.log("updateModelData data", modelData);
      setmodelDataLoading(false);
      setmodelData(modelData);
    };
    try {
      deviceModelApi.getDeviceModels(
        apiCallback({ history, errorCallback, successCallback })
      );
    } catch (error) {
      errorCallback(error);
    }
  };
  const updateGroupList = (id) => {
    const providerId = id || selectedProvider;
    if (!providerId) return;
    const { history, groupApi } = props;
    setGroupDataLoading(true);
    const errorCallback = (error) => {
      setGroupDataLoading(false);
      message.error("Error fetching Group list");
    };
    const successCallback = (data) => {
      const groupData = convertGroupListToDropdown(data).filter(
        (item) => item.purpose === GroupPurpose.entitlement.value
      );
      console.log("updateDataList data", groupData);
      setGroupDataLoading(false);
      setGroupData(groupData);
    };
    try {
      groupApi.getGroupsByProviderId(
        providerId,
        apiCallback({ history, errorCallback, successCallback })
      );
    } catch (error) {
      errorCallback(error);
    }
  };
  const onProviderChange = (id) => {
    setSelectedProvider(id);
    id && updateGroupList(id);
    form.setFieldsValue({ groupId: null });
  };

  const onBackClick = () => {
    const isChanged =
      action === ConfigActions.view ? false : checkForInputChanges();
    DiscardWarning({ isChanged, onActionClick })();
  };

  const checkForInputChanges = () => {
    const fieldsToCheck = [
      "name",
      "providerId",
      "groupId",
      "deviceId",
      "serviceId",
      "config",
      "status",
    ];
    const values = form.getFieldsValue();
    let isInputsChanged = false;
    fieldsToCheck.every((f) => {
      if (
        values.hasOwnProperty(f) &&
        values[f] !== initialValues[f] &&
        (!!values[f] || !!initialValues[f])
      ) {
        isInputsChanged = true;
        return false;
      }
      return true;
    });
    return isInputsChanged;
  };

  return (
    <div className="list-container">
      <div className="page-header-container">
        <BreadCrumbs
          headers={props.parentBread.concat(`${title} Group Configuration`)}
          onParentActionClick={onBackClick}
        />
      </div>
      <Divider className="divider" />
      <div className="form-data-container">
        <fieldset disabled={isViewForm}>
          <Form
            form={form}
            layout="horizontal"
            name="group-config-form"
            initialValues={initialValues}
            requiredMark={"optional"}
            {...formItemLayout}
            size={"middle"}
            onFinish={onFormSubmit({
              props,
              setProcessing,
              setInitialValues,
              entity: ConfigEntity.group,
            })}
          >
            <Spin spinning={processing} tip={"Processing, please wait..."}>
              <Form.Item
                name="name"
                label="Configuration Name"
                rules={[
                  { required: true, message: "Please fill in this field" },
                ]}
              >
                <Input
                  title="Name should be unique"
                  style={{ width: "90%", maxWidth: "300px" }}
                  disabled={!isAddForm}
                />
              </Form.Item>

              <Form.Item
                name="providerId"
                label="Service Provider:"
                rules={[
                  { required: true, message: "Please select Service Provider" },
                ]}
              >
                <Select
                  showSearch
                  allowClear
                  style={{ width: "90%", maxWidth: "350px" }}
                  loading={props.providerDataLoading}
                  disabled={props.providerDataLoading || !isAddForm}
                  filterOption={selectFilterFunction}
                  onChange={onProviderChange}
                  placeholder={
                    props.providerDataLoading
                      ? "Loading Service Provider list..."
                      : "Select Service Provider"
                  }
                >
                  {props.providerData?.map((item) => (
                    <Select.Option
                      key={item.providerId}
                      title={item.providerName}
                    >
                      {item.providerName}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="groupId"
                label="Group:"
                rules={[{ required: true, message: "Please select Group" }]}
              >
                <Select
                  showSearch
                  allowClear
                  style={{ width: "90%", maxWidth: "350px" }}
                  loading={groupDataLoading}
                  disabled={groupDataLoading || !selectedProvider || !isAddForm}
                  filterOption={selectFilterFunction}
                  placeholder={
                    !selectedProvider
                      ? "Select Provider to view Groups"
                      : groupDataLoading
                      ? "Loading Group list..."
                      : "Select Group"
                  }
                >
                  {groupData?.map((item) => (
                    <Select.Option key={item.groupId} title={item.groupName}>
                      {item.groupName}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="serviceId"
                label="Service:"
                rules={[{ required: true, message: "Please select Service" }]}
              >
                <Select
                  showSearch
                  allowClear
                  style={{ width: "90%", maxWidth: "350px" }}
                  loading={props.serviceDataLoading}
                  disabled={props.serviceDataLoading || !isAddForm}
                  filterOption={selectFilterFunction}
                  placeholder={
                    serviceDataLoading
                      ? "Loading Service list..."
                      : "Select Service"
                  }
                >
                  {serviceData?.map((item) => (
                    <Select.Option
                      key={item.serviceId}
                      title={item.serviceName}
                    >
                      {item.serviceName}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="modelId"
                label="Device Model:"
                rules={[
                  { required: true, message: "Please select Device Model" },
                ]}
              >
                <Select
                  showSearch
                  allowClear
                  style={{ width: "90%", maxWidth: "350px" }}
                  loading={props.modelDataLoading}
                  disabled={props.modelDataLoading || !isAddForm}
                  filterOption={selectFilterFunction}
                  placeholder={
                    modelDataLoading
                      ? "Loading Device Model list..."
                      : "Select Device Model"
                  }
                >
                  {modelData?.map((item) => (
                    <Select.Option key={item.modelId} title={item.modelNumber}>
                      {item.modelNumber}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="config"
                label="Configuration"
                validateTrigger={["onBlur"]}
                rules={[{ required: true, validator: jsonValidator() }]}
              >
                <Input.TextArea
                  title="Configuration must be JSON object"
                  autoSize={{ minRows: 4, maxRows: 8 }}
                  style={{ width: "90%", maxWidth: "500px" }}
                />
              </Form.Item>

              <Form.Item
                name="status"
                label="Status"
                rules={[{ required: true }]}
              >
                <Tag
                  color={
                    initialValues.status === EntityStatus.ACTIVE
                      ? "green"
                      : "red"
                  }
                >
                  {initialValues.status}
                </Tag>
              </Form.Item>

              {!isViewForm && (
                <Form.Item {...tailItemLayout}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    className="submit-button"
                  >
                    {title}
                  </Button>
                  <Button
                    htmlType="button"
                    onClick={() => form.resetFields()}
                    className="cancel-button"
                  >
                    Cancel
                  </Button>
                </Form.Item>
              )}
            </Spin>
          </Form>
        </fieldset>
      </div>
    </div>
  );
};

GroupConfigForm.propTypes = {
  action: PropTypes.string,
  record: PropTypes.object,
  onSubmit: PropTypes.func,
  onActionClick: PropTypes.func,
  providerData: PropTypes.array,
  providerDataLoading: PropTypes.bool,
  parentBread: PropTypes.array,

  groupApi: PropTypes.object,
  serviceApi: PropTypes.object,
  deviceModelApi: PropTypes.object,
  configManagementApi: PropTypes.object,
};

const mapStateToProps = (state) => ({
  groupApi: state.apis.groupApi,
  serviceApi: state.apis.serviceApi,
  deviceModelApi: state.apis.deviceModelApi,
  configManagementApi: state.apis.configManagementApi,
});
const connectedGroupConfigForm = connect(mapStateToProps)(GroupConfigForm);
export default withRouter(connectedGroupConfigForm);
